/**
 * Browser-only helpers for Microsoft Clarity.
 * Fire-and-forget: never throws to callers.
 */
import Clarity from "@microsoft/clarity";

export type ClarityWhatsAppIntentType = "chat_to_buy" | "payment_receipt";
export type ClarityRegistrationMethod = "email_verification" | "oauth_profile";
export type ClarityNewsletterSource =
  | "onboarding_tour_actions"
  | "profile_settings"
  | "sell_success";
export type ClarityOnboardingTourSource =
  | "homepage_autoshow"
  | "floating_help"
  | "profile_tab";
export type ClarityOnboardingTourAudience = "guest" | "auth";
export type ClarityOnboardingTourLifecycle = "start" | "skip" | "complete";

function safeClarityEvent(eventName: string): void {
  if (typeof window === "undefined") return;
  try {
    Clarity.event(eventName);
  } catch {
    // Intentionally ignore analytics failures
  }
}

function safeClaritySetTag(tag: string, value: string): void {
  if (typeof window === "undefined") return;
  const normalizedValue = value.trim();
  if (!normalizedValue) return;

  try {
    Clarity.setTag(tag, normalizedValue);
  } catch {
    // Intentionally ignore analytics failures
  }
}

function sanitizeTagValue(value: string, max = 120): string {
  return value.trim().slice(0, max);
}

function normalizePriceTag(valueNgn: number): string {
  if (!Number.isFinite(valueNgn) || valueNgn < 0) return "unknown";
  if (valueNgn < 50000) return "under_50k";
  if (valueNgn < 150000) return "50k_to_149k";
  if (valueNgn < 300000) return "150k_to_299k";
  return "300k_plus";
}

export function trackClarityViewContent(input: {
  itemId: string;
  title: string;
  valueNgn: number;
}): void {
  const { itemId, title, valueNgn } = input;
  if (!itemId) return;

  safeClarityEvent("item_view");
  safeClaritySetTag("item_id", sanitizeTagValue(itemId, 80));
  safeClaritySetTag("item_title", sanitizeTagValue(title));
  safeClaritySetTag("item_price_band", normalizePriceTag(valueNgn));
  safeClaritySetTag("currency", "NGN");
}

export function trackClaritySearch(searchString: string): void {
  const q = searchString.trim();
  if (!q) return;

  safeClarityEvent("market_search");
  safeClaritySetTag("search_query", sanitizeTagValue(q, 100));
  safeClaritySetTag("search_length", String(q.length));
}

export function trackClarityWhatsAppIntent(input: {
  itemId: string;
  title: string;
  valueNgn: number;
  intentType: ClarityWhatsAppIntentType;
}): void {
  const { itemId, title, valueNgn, intentType } = input;
  if (!itemId) return;

  safeClarityEvent("whatsapp_intent");
  safeClaritySetTag("whatsapp_intent_type", intentType);
  safeClaritySetTag("item_id", sanitizeTagValue(itemId, 80));
  safeClaritySetTag("item_title", sanitizeTagValue(title));
  safeClaritySetTag("item_price_band", normalizePriceTag(valueNgn));
}

export function trackClarityLead(input: {
  contentName: string;
  contentCategory: string;
  valueNgn?: number;
}): void {
  const name = input.contentName.trim();
  if (!name) return;

  safeClarityEvent("item_request_submitted");
  safeClaritySetTag("request_title", sanitizeTagValue(name));
  safeClaritySetTag(
    "request_category",
    sanitizeTagValue(input.contentCategory, 60),
  );

  if (
    typeof input.valueNgn === "number" &&
    Number.isFinite(input.valueNgn) &&
    input.valueNgn >= 0
  ) {
    safeClaritySetTag("request_budget_band", normalizePriceTag(input.valueNgn));
  }
}

export function trackClaritySubmitListing(input: {
  itemId: string;
  title: string;
  valueNgn: number;
}): void {
  const { itemId, title, valueNgn } = input;
  if (!itemId) return;

  safeClarityEvent("listing_submitted");
  safeClaritySetTag("listing_item_id", sanitizeTagValue(itemId, 80));
  safeClaritySetTag("listing_title", sanitizeTagValue(title));
  safeClaritySetTag("listing_price_band", normalizePriceTag(valueNgn));
}

export function trackClarityCompleteRegistration(
  method: ClarityRegistrationMethod,
): void {
  safeClarityEvent("registration_completed");
  safeClaritySetTag("registration_method", method);
}

export function trackClarityNewsletterPopupClick(
  source: ClarityNewsletterSource,
): void {
  safeClarityEvent("newsletter_popup_click");
  safeClaritySetTag("newsletter_source", source);
}

export function trackClarityOnboardingTourOpen(input: {
  source: ClarityOnboardingTourSource;
  audience: ClarityOnboardingTourAudience;
}): void {
  safeClarityEvent("onboarding_tour_open");
  safeClaritySetTag("onboarding_tour_source", input.source);
  safeClaritySetTag("onboarding_tour_audience", input.audience);
}

export function trackClarityOnboardingTourLifecycle(input: {
  source: ClarityOnboardingTourSource;
  audience: ClarityOnboardingTourAudience;
  event: ClarityOnboardingTourLifecycle;
}): void {
  safeClarityEvent(`onboarding_tour_${input.event}`);
  safeClaritySetTag("onboarding_tour_source", input.source);
  safeClaritySetTag("onboarding_tour_audience", input.audience);
}
